"use client";
import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause } from "lucide-react";
import w1 from "@/assets/work-1.jpg";
import w2 from "@/assets/work-2.jpg";
import w3 from "@/assets/work-3.jpg";
import w4 from "@/assets/work-4.jpg";

const ITEMS = [
  { 
    id: 1, 
    img: w1, 
    title: "Halogen / 2025", 
    tag: "Album Production", 
    span: "md:col-span-7", 
    audio: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3" 
  },
  { 
    id: 2, 
    img: w2, 
    title: "Vesper / 2025", 
    tag: "Sound Identity", 
    span: "md:col-span-5", 
    audio: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-2.mp3" 
  },
  { 
    id: 3, 
    img: w3, 
    title: "Atrium / 2024", 
    tag: "Mastering", 
    span: "md:col-span-5", 
    audio: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-3.mp3" 
  },
  { 
    id: 4, 
    img: w4, 
    title: "Static.fm / 2024", 
    tag: "Vocal Recording", 
    span: "md:col-span-7", 
    audio: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-4.mp3" 
  },
];

function Waveform({ isPlaying }: { isPlaying: boolean }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
      <svg viewBox="0 0 400 100" preserveAspectRatio="none" className="w-full h-full opacity-60">
        <motion.path
          d="M 0 50 C 50 50, 100 50, 150 50 C 200 50, 250 50, 300 50 C 350 50, 400 50, 400 50"
          stroke="var(--acid)"
          strokeWidth="1.5"
          fill="none"
          animate={isPlaying ? {
            d: [
              "M 0 50 C 50 20, 100 80, 150 50 C 200 20, 250 80, 300 50 C 350 20, 400 80, 400 50",
              "M 0 50 C 50 80, 100 20, 150 50 C 200 80, 250 20, 300 50 C 350 80, 400 20, 400 50",
              "M 0 50 C 50 20, 100 80, 150 50 C 200 20, 250 80, 300 50 C 350 20, 400 80, 400 50",
            ]
          } : {
            d: "M 0 50 C 50 50, 100 50, 150 50 C 200 50, 250 50, 300 50 C 350 50, 400 50, 400 50"
          }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
        />
        <motion.path
          d="M 0 50 C 50 50, 100 50, 150 50 C 200 50, 250 50, 300 50 C 350 50, 400 50, 400 50"
          stroke="var(--acid)"
          strokeWidth="1"
          opacity="0.4"
          fill="none"
          animate={isPlaying ? {
            d: [
              "M 0 50 C 50 80, 100 20, 150 50 C 200 80, 250 20, 300 50 C 350 80, 400 20, 400 50",
              "M 0 50 C 50 20, 100 80, 150 50 C 200 20, 250 80, 300 50 C 350 20, 400 80, 400 50",
              "M 0 50 C 50 80, 100 20, 150 50 C 200 80, 250 20, 300 50 C 350 80, 400 20, 400 50",
            ]
          } : {
            d: "M 0 50 C 50 50, 100 50, 150 50 C 200 50, 250 50, 300 50 C 350 50, 400 50, 400 50"
          }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        />
      </svg>
    </div>
  );
}

function ArtifactCard({ item, activeId, setActiveId }: { item: typeof ITEMS[0], activeId: number | null, setActiveId: (id: number | null) => void }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [progress, setProgress] = useState(0);
  const isPlaying = activeId === item.id;

  useEffect(() => {
    if (isPlaying) {
      audioRef.current?.play();
    } else {
      audioRef.current?.pause();
      if (audioRef.current) audioRef.current.currentTime = 0;
    }
  }, [isPlaying]);

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const p = (audioRef.current.currentTime / audioRef.current.duration) * 100;
      setProgress(p);
    }
  };

  const togglePlay = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isPlaying) {
      setActiveId(null);
    } else {
      setActiveId(item.id);
    }
  };

  return (
    <figure className={`col-span-12 ${item.span} group relative`}>
      <div className="tilt overflow-hidden rounded-md aspect-[4/3] bg-card relative">
        <img
          src={item.img.src}
          alt={item.title}
          loading="lazy"
          width={1280}
          height={900}
          className={`w-full h-full object-cover transition-all duration-700 ${isPlaying ? 'scale-110 blur-sm brightness-50' : 'group-hover:scale-105'}`}
        />
        
        <AnimatePresence>
          {isPlaying && <Waveform isPlaying={isPlaying} />}
        </AnimatePresence>

        {/* Play/Pause Button Overlay */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <button
            onClick={togglePlay}
            className="w-20 h-20 rounded-full glass border border-white/10 flex items-center justify-center backdrop-blur-md transition-transform hover:scale-110 active:scale-95"
          >
            {isPlaying ? (
              <Pause className="w-8 h-8 text-[oklch(0.94_0.27_128)] fill-[oklch(0.94_0.27_128)]" />
            ) : (
              <Play className="w-8 h-8 text-[oklch(0.94_0.27_128)] fill-[oklch(0.94_0.27_128)] ml-1" />
            )}
          </button>
        </div>

        {/* Progress Bar */}
        <div className="absolute bottom-0 left-0 w-full h-[2px] bg-white/10 overflow-hidden">
          <motion.div 
            className="h-full bg-[oklch(0.94_0.27_128)]"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ type: "spring", bounce: 0, duration: 0.1 }}
          />
        </div>

        <audio
          ref={audioRef}
          src={item.audio}
          onTimeUpdate={handleTimeUpdate}
          onEnded={() => setActiveId(null)}
          hidden
        />
      </div>

      <figcaption className="mt-4 flex items-center justify-between font-mono text-xs uppercase tracking-widest">
        <span className="text-foreground">→ {item.title}</span>
        <span className="text-[oklch(0.94_0.27_128)]">{item.tag}</span>
      </figcaption>
    </figure>
  );
}

export function Work() {
  const [activeId, setActiveId] = useState<number | null>(null);

  return (
    <section id="work" className="relative px-6 md:px-12 py-32 border-t border-border">
      <div className="max-w-[1600px] mx-auto">
        <div className="flex items-end justify-between mb-16">
          <h2 className="font-display uppercase leading-[0.85]"
            style={{ fontSize: "clamp(3rem, 8vw, 9rem)" }}>
            Sonic
            <br />
            <span className="text-outline">artifacts.</span>
          </h2>
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground hidden md:block">
            [003] / 04 — featured
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {ITEMS.map((it) => (
            <ArtifactCard 
              key={it.id} 
              item={it} 
              activeId={activeId} 
              setActiveId={setActiveId} 
            />
          ))}
        </div>
      </div>
    </section>
  );
}

